import axios from "axios";
import { buildInlineExternalFormEmbedScript, buildInlineFileRequestEmbedScript, DEFAULT_EXTERNAL_FORM_RENDERER_KEY, EXTERNAL_FORM_RENDERER_KEYS } from "@/Utile/ncrmExternalFormRenderers.mjs";
import { DEFAULT_FILE_REQUEST_LOCALES, FILE_REQUEST_FORMATS, normalizeFileRequestFormats, normalizeFileRequestLocales } from "@/Utile/ncrmExternalFileFormConfig.mjs";
import { buildExternalFormWidgetPayloadConfig } from "@/Utile/ncrmExternalFormWidgetPayload.mjs";
export { buildInlineExternalFormEmbedScript, buildInlineFileRequestEmbedScript, DEFAULT_EXTERNAL_FORM_RENDERER_KEY, EXTERNAL_FORM_RENDERER_KEYS, INLINE_DEMO_REQUEST_RENDERER_KEY, INLINE_FILE_REQUEST_RENDERER_KEY } from "@/Utile/ncrmExternalFormRenderers.mjs";

export const DEFAULT_EXTERNAL_FORM_ORIGINS = Object.freeze([
  "https://saitultau1.md",
  "https://saitultau2.md",
]);

export const EXTERNAL_FORM_WIDGET_STATUSES = Object.freeze(["draft", "active", "paused", "archived"]);
export const DEFAULT_EXTERNAL_FORM_DISPLAY_CONFIG = Object.freeze({
  rendererKey: DEFAULT_EXTERNAL_FORM_RENDERER_KEY,
  delaySeconds: 20,
  repeatMode: "daily",
  repeatAfterHours: 24,
  includePages: ["*"],
  excludePages: [],
  allowedFileExtensions: [...FILE_REQUEST_FORMATS],
});

export const DEFAULT_EXTERNAL_FORM_CONTENT = Object.freeze({
  eyebrow: "Consultanta MiriDar",
  title: "Vrei sa te contactam?",
  titleAccent: "Lasati-ne datele si revenim rapid",
  subtitle: "Trimite numele si telefonul, iar echipa MiriDar te contacteaza in scurt timp.",
  nameLabel: "Nume",
  namePlaceholder: "Numele dvs.",
  phoneLabel: "Telefon",
  phonePlaceholder: "+373 ...",
  commentLabel: "Comentariu",
  commentPlaceholder: "Cand sa va sunam? sau alte informatii",
  consentLabel: "Sunt de acord sa fiu contactat de MiriDar in baza datelor transmise.",
  countryLabel: "Tara",
  countryPlaceholder: "Moldova",
  preferredCallTimeLabel: "Cand sa va sunam?",
  preferredCallTimeOptions: [
    { value: "asap", label: "Cat mai curand" },
    { value: "today_pm", label: "Astazi dupa amiaza" },
    { value: "tomorrow_am", label: "Maine dimineata" },
    { value: "tomorrow_pm", label: "Maine dupa amiaza" },
  ],
  submitLabel: "Trimite",
  submittingLabel: "Se trimite...",
  successTitle: "Multumim",
  successBody: "Am primit datele si revenim in scurt timp.",
  directChannelsLabel: "sau contacteaza-ne direct",
  dismissLabel: "Inchide",
  closeLabel: "Inchide",
  microcopy: "Datele sunt folosite doar pentru a va contacta.",
  errorGeneric: "Nu am putut trimite formularul. Incercati din nou.",
  fileRequestLocales: normalizeFileRequestLocales(DEFAULT_FILE_REQUEST_LOCALES),
});

export const DEFAULT_EXTERNAL_FORM_THEME = Object.freeze({
  primaryColor: "#0f766e",
  accentColor: "#24c8c6",
  backgroundColor: "#ffffff",
  textColor: "#14202d",
  mutedTextColor: "#637083",
});

export const DEFAULT_EXTERNAL_FORM_FIELDS = Object.freeze({
  name: { enabled: true, required: true },
  phone: { enabled: true, required: true },
  comment: { enabled: true, required: false },
  preferred_call_time: { enabled: false, required: false },
  country: { enabled: false, required: false },
});

export const DEFAULT_EXTERNAL_FORM_CHANNELS = Object.freeze({
  whatsapp: { enabled: false, label: "WhatsApp", phone: "", message: "" },
  telegram: { enabled: false, label: "Telegram", link: "" },
  viber: { enabled: false, label: "Viber", phone: "", link: "" },
  email: { enabled: false, label: "Email", address: "", subject: "", body: "" },
});

export const DEFAULT_EXTERNAL_FORM_CONSENT = Object.freeze({
  required: true,
  label: "Sunt de acord sa fiu contactat de MiriDar in baza datelor transmise.",
});

export const DEFAULT_EXTERNAL_FORM_NOTIFICATIONS = Object.freeze({
  email: {
    enabled: false,
    to: [],
    cc: [],
    subject: "Cerere noua demo MiriDar",
  },
  telegram: {
    enabled: false,
  },
});

const deepClone = (value) => JSON.parse(JSON.stringify(value));

export const slugifyExternalFormKey = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 96);

export const parseExternalFormOriginsText = (value) =>
  String(value || "")
    .split(/[\n,]+/)
    .map((origin) => origin.trim().replace(/\/+$/, ""))
    .filter(Boolean);

export const formatExternalFormOriginsText = (origins = []) =>
  (Array.isArray(origins) ? origins : []).join("\n");

const resolveRendererKey = (value) =>
  EXTERNAL_FORM_RENDERER_KEYS.includes(value) ? value : DEFAULT_EXTERNAL_FORM_RENDERER_KEY;

export const createExternalFormWidgetDraft = ({ workflowInstanceId = "", endpointId = null } = {}) => ({
  id: null,
  workflow_instance_id: workflowInstanceId,
  endpoint_id: endpointId,
  form_key: "",
  name: "",
  status: "draft",
  rendererKey: DEFAULT_EXTERNAL_FORM_RENDERER_KEY,
  allowedOrigins: [...DEFAULT_EXTERNAL_FORM_ORIGINS],
  displayConfig: deepClone(DEFAULT_EXTERNAL_FORM_DISPLAY_CONFIG),
  content: deepClone(DEFAULT_EXTERNAL_FORM_CONTENT),
  theme: deepClone(DEFAULT_EXTERNAL_FORM_THEME),
  fields: deepClone(DEFAULT_EXTERNAL_FORM_FIELDS),
  channels: deepClone(DEFAULT_EXTERNAL_FORM_CHANNELS),
  consent: deepClone(DEFAULT_EXTERNAL_FORM_CONSENT),
  notifications: deepClone(DEFAULT_EXTERNAL_FORM_NOTIFICATIONS),
  endpoint: null,
  embedScript: "",
  inlineEmbedScript: "",
  fileRequestEmbedScript: "",
});

export const normalizeExternalFormWidget = (widget = {}, fallback = {}) => {
  const displayConfig = {
    ...deepClone(DEFAULT_EXTERNAL_FORM_DISPLAY_CONFIG),
    ...(widget.displayConfig || widget.display_config || {}),
  };
  const rendererKey = resolveRendererKey(widget.rendererKey || widget.renderer_key || displayConfig.rendererKey);
  displayConfig.rendererKey = rendererKey;
  displayConfig.allowedFileExtensions = normalizeFileRequestFormats(displayConfig.allowedFileExtensions);
  const formKey = widget.form_key || widget.formKey || "";
  return {
    ...createExternalFormWidgetDraft(fallback),
    ...deepClone(widget || {}),
    workflow_instance_id: widget.workflow_instance_id || widget.workflowInstanceId || fallback.workflowInstanceId || "",
    endpoint_id: widget.endpoint_id || widget.endpointId || fallback.endpointId || null,
    form_key: formKey,
    rendererKey,
    allowedOrigins: Array.isArray(widget.allowedOrigins)
      ? [...widget.allowedOrigins]
      : Array.isArray(widget.allowed_origins)
        ? [...widget.allowed_origins]
        : [...DEFAULT_EXTERNAL_FORM_ORIGINS],
    displayConfig,
    content: {
      ...deepClone(DEFAULT_EXTERNAL_FORM_CONTENT),
      ...(widget.content || {}),
      fileRequestLocales: normalizeFileRequestLocales(widget.content?.fileRequestLocales),
      preferredCallTimeOptions:
        widget.content?.preferredCallTimeOptions?.length
          ? widget.content.preferredCallTimeOptions.map((item) => ({ ...item }))
          : deepClone(DEFAULT_EXTERNAL_FORM_CONTENT.preferredCallTimeOptions),
    },
    theme: {
      ...deepClone(DEFAULT_EXTERNAL_FORM_THEME),
      ...(widget.theme || {}),
    },
    fields: {
      ...deepClone(DEFAULT_EXTERNAL_FORM_FIELDS),
      ...(widget.fields || {}),
      comment: {
        ...DEFAULT_EXTERNAL_FORM_FIELDS.comment,
        ...(widget.fields?.comment || widget.fields?.country || {}),
      },
      preferred_call_time: {
        ...DEFAULT_EXTERNAL_FORM_FIELDS.preferred_call_time,
        enabled: false,
        required: false,
      },
      country: {
        ...DEFAULT_EXTERNAL_FORM_FIELDS.country,
        enabled: false,
        required: false,
      },
    },
    channels: {
      whatsapp: { ...DEFAULT_EXTERNAL_FORM_CHANNELS.whatsapp, ...(widget.channels?.whatsapp || {}) },
      telegram: { ...DEFAULT_EXTERNAL_FORM_CHANNELS.telegram, ...(widget.channels?.telegram || {}) },
      viber: { ...DEFAULT_EXTERNAL_FORM_CHANNELS.viber, ...(widget.channels?.viber || {}) },
      email: { ...DEFAULT_EXTERNAL_FORM_CHANNELS.email, ...(widget.channels?.email || {}) },
    },
    consent: {
      ...deepClone(DEFAULT_EXTERNAL_FORM_CONSENT),
      ...(widget.consent || {}),
    },
    notifications: {
      email: {
        ...deepClone(DEFAULT_EXTERNAL_FORM_NOTIFICATIONS.email),
        ...(widget.notifications?.email || {}),
        to: Array.isArray(widget.notifications?.email?.to) ? [...widget.notifications.email.to] : [],
        cc: Array.isArray(widget.notifications?.email?.cc) ? [...widget.notifications.email.cc] : [],
      },
      telegram: {
        ...deepClone(DEFAULT_EXTERNAL_FORM_NOTIFICATIONS.telegram),
        ...(widget.notifications?.telegram || {}),
        enabled: Boolean(widget.notifications?.telegram?.enabled),
        recipientUserIds: Array.isArray(widget.notifications?.telegram?.recipientUserIds)
          ? [...widget.notifications.telegram.recipientUserIds]
          : [],
      },
    },
    inlineEmbedScript: widget.inlineEmbedScript || widget.embedScripts?.inline || buildInlineExternalFormEmbedScript(formKey),
    fileRequestEmbedScript:
      widget.fileRequestEmbedScript ||
      widget.embedScripts?.fileRequest ||
      buildInlineFileRequestEmbedScript(formKey),
  };
};

export const buildExternalFormWidgetPayload = (form = {}) =>
  buildExternalFormWidgetPayloadConfig(form, {
    rendererKey: DEFAULT_EXTERNAL_FORM_RENDERER_KEY,
    rendererKeys: EXTERNAL_FORM_RENDERER_KEYS,
    statuses: EXTERNAL_FORM_WIDGET_STATUSES,
    origins: DEFAULT_EXTERNAL_FORM_ORIGINS,
    displayConfig: DEFAULT_EXTERNAL_FORM_DISPLAY_CONFIG,
    content: DEFAULT_EXTERNAL_FORM_CONTENT,
    theme: DEFAULT_EXTERNAL_FORM_THEME,
    fields: DEFAULT_EXTERNAL_FORM_FIELDS,
    channels: DEFAULT_EXTERNAL_FORM_CHANNELS,
    consent: DEFAULT_EXTERNAL_FORM_CONSENT,
    notifications: DEFAULT_EXTERNAL_FORM_NOTIFICATIONS,
  });

export const fetchExternalFormWidgets = (workflowInstanceId) =>
  axios.get("/ncrm/workflows/external-form-widgets", {
    params: workflowInstanceId ? { workflow_instance_id: workflowInstanceId } : {},
  });

export const createExternalFormWidget = (payload) => axios.post("/ncrm/workflows/external-form-widgets", payload);
export const updateExternalFormWidget = (widgetId, payload) => axios.patch(`/ncrm/workflows/external-form-widgets/${widgetId}`, payload);
